<template>
  <v-app>
    <v-container class="gradient-background">
      <!-- Header -->
      <v-row justify="center" class="mb-4">
        <v-col cols="12" class="text-center">
          <h1 class="white--text text-h4 font-weight-bold">Groups</h1>
        </v-col>
      </v-row>

      <!-- Tabs -->
      <v-row justify="center">
        <v-col cols="12" md="8">
          <v-tabs v-model="currentTab" background-color="#3b5d58" dark centered slider-color="white">
            <v-tab>Public Groups</v-tab>
            <v-tab>My Private Groups</v-tab>
          </v-tabs>

          <v-tabs-items v-model="currentTab">
            <!-- Public Groups -->
            <v-tab-item>
              <v-row>
                <v-col
                  v-for="group in publicGroups"
                  :key="group.id"
                  cols="12"
                  md="6"
                  lg="4"
                >
                  <v-card class="group-card">
                    <v-img
                      :src="group.imageURL || defaultImage"
                      height="200"
                      class="rounded-t-xl"
                    ></v-img>
                    <v-card-title>{{ group.name }}</v-card-title>
                    <v-card-subtitle class="text-truncate">{{ group.description }}</v-card-subtitle>
                    <v-card-actions class="d-flex flex-column">
                      <v-btn class="soft-button mb-2" block @click="viewGroup(group)">
                        View Group
                      </v-btn>
                      <v-btn
                        v-if="!isUserInGroup(group)"
                        class="soft-button"
                        color="primary"
                        block
                        @click="joinGroup(group.id)"
                      >
                        Join Group
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- Private Groups -->
            <v-tab-item>
              <v-row>
                <v-col
                  v-for="group in privateGroups"
                  :key="group.id"
                  cols="12"
                  md="6"
                  lg="4"
                >
                  <v-card class="group-card">
                    <v-img
                      :src="group.imageURL || defaultImage"
                      height="200"
                      class="rounded-t-xl"
                    ></v-img>
                    <v-card-title>{{ group.name }}</v-card-title>
                    <v-card-subtitle class="text-truncate">{{ group.description }}</v-card-subtitle>
                    <v-card-actions>
                      <v-btn class="soft-button" block @click="viewGroup(group)">
                        View Group
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>
    </v-container>

    <!-- FAB -->
    <v-btn fab color="white" class="floating-btn" elevation="10" @click="$router.push('/groups/create')">
      <v-icon>mdi-plus</v-icon>
    </v-btn>

    <!-- Bottom Nav -->
    <v-bottom-navigation app background-color="#3b5d58" grow dark>
      <v-btn icon to="/home"><v-icon>mdi-home</v-icon><span class="tab-label">Home</span></v-btn>
      <v-btn icon to="/search"><v-icon>mdi-magnify</v-icon><span class="tab-label">Search</span></v-btn>
      <v-btn icon to="/explore"><v-icon>mdi-compass-outline</v-icon><span class="tab-label">Explore</span></v-btn>
      <v-btn icon to="/messages"><v-icon>mdi-message-text-outline</v-icon><span class="tab-label">Messages</span></v-btn>
      <v-btn icon to="/groups"><v-icon>mdi-account-group</v-icon><span class="tab-label">Groups</span></v-btn>
      <v-btn icon @click="$goToProfile()">
        <span>
          <v-icon>mdi-account</v-icon>
          <p class="tab-label">Profile</p>
        </span>
      </v-btn>
    </v-bottom-navigation>
  </v-app>
</template>

<script>
import { getAuth } from "firebase/auth";
import { collection, getDocs, addDoc, doc, updateDoc } from "firebase/firestore";
import { db } from "@/firebase";

export default {
  data() {
    return {
      publicGroups: [],
      privateGroups: [],
      defaultImage: "https://via.placeholder.com/200",
      currentTab: 0,
      userUID: null,
      showCreateGroupModal: false,
      groupName: "",
      groupDescription: "",
      isPublic: false,
      groupImage: null,
      members: [],
      selectedMembers: [],
    };
  },
  methods: {
    async fetchGroups() {
      const groupsRef = collection(db, "groups");
      const groupsSnap = await getDocs(groupsRef);
      const allGroups = groupsSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      this.publicGroups = allGroups.filter((group) => group.isPublic);
      this.privateGroups = allGroups.filter(
        (group) =>
          !group.isPublic &&
          group.members &&
          group.members.includes(this.userUID)
      );
    },
    viewGroup(group) {
      this.$router.push(`/groups/${group.id}`);
    },
    async fetchUserUID() {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (currentUser) {
        this.userUID = currentUser.uid;
      }
    },
    async fetchMembers() {
      const membersRef = collection(db, "user");
      const membersSnap = await getDocs(membersRef);
      this.members = membersSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    },
    openFilePicker() {
      this.$refs.fileInput.click();
    },
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.groupImage = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    async createGroup() {
      const group = {
        name: this.groupName,
        description: this.groupDescription,
        isPublic: this.isPublic,
        members: this.selectedMembers,
        imageURL: this.groupImage,
      };
      await addDoc(collection(db, "groups"), group);
      this.showCreateGroupModal = false;
      this.fetchGroups();
    },
    async joinGroup(groupId) {
      const groupRef = doc(db, "groups", groupId);
      const groupDoc = await getDoc(groupRef);

      if (!groupDoc.exists()) return;

      const groupData = groupDoc.data();
      const currentMembers = groupData.members || [];

      if (!currentMembers.includes(this.userUID)) {
        const updatedMembers = [...currentMembers, this.userUID];
        await updateDoc(groupRef, { members: updatedMembers });
        this.fetchGroups();
        alert("You have joined the group!");
      }
    },
    isUserInGroup(group) {
      return group.members && group.members.includes(this.userUID);
    },
  },
  async mounted() {
    await this.fetchUserUID();
    this.fetchGroups();
    this.fetchMembers();
  },
};
</script>

<style scoped>
.gradient-background {
  background: linear-gradient(to bottom, #f2c46d, #3b5d58);
  min-height: 100vh;
  padding-top: 24px;
  padding-bottom: 100px;
}

.floating-btn {
  position: fixed;
  bottom: 90px;
  right: 20px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);
  z-index: 999;
}

.group-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
.group-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2);
}

.soft-button {
  background: linear-gradient(to bottom, #f2c46d, #3b5d58);
  color: bisque;
  font-weight: bold;
  border-radius: 8px;
  text-transform: none;
  font-weight: bolder;
}
.soft-button:hover {
  filter: brightness(1.1);
}

.tab-label {
  font-size: 11px;
  text-align: center;
  margin-top: 2px;
  color: white;
}
</style>